import { useEffect, useState } from 'react';
import { v4 as generateId } from 'uuid';
import { mutate } from 'swr';

import container from 'src/container';
import { Organization } from 'src/domain/organization';
import avatarDefault from 'src/assets/images/client_default.png';
import { useAvatar } from './useAvatar';

const {
    cradle: { storageService, munduClient },
} = container;

export const useUploadOrganizationAvatar = (
    organization?: Organization,
    kind: 'organization' | 'superuser' = 'organization',
) => {
    const isSuperuserAvatar = kind === 'superuser';
    const { src } = useAvatar(
        isSuperuserAvatar
            ? organization?.superuserAvatarUrl
                ? undefined
                : organization?.superuserAvatar
            : organization?.avatarUrl
            ? undefined
            : organization?.profile.avatar,
        {
            firstName: organization?.profile.firstName,
            lastName: organization?.profile.lastName,
            displayName: organization?.organizationDisplayName,
        },
    );
    const resolvedSrc = isSuperuserAvatar
        ? organization?.superuserAvatarUrl || src
        : organization?.avatarUrl || src;
    const [preview, setPreview] = useState<string>(resolvedSrc);
    const [file, setFile] = useState<File>();

    const uploadAvatar = async (
        fileToUpload?: File,
        targetOrganization?: Organization,
    ) => {
        const resolvedOrganization = targetOrganization ?? organization;
        if (!fileToUpload || !resolvedOrganization) return;
        const targetAvatarSAS = await munduClient.getAvatarSas(
            resolvedOrganization.id,
        );
        const fileName = generateId();
        await storageService.uploadImage(
            fileToUpload,
            fileName,
            targetAvatarSAS,
        );
        await munduClient.updateOganization(
            resolvedOrganization.id,
            isSuperuserAvatar
                ? { superuserAvatar: fileName }
                : { avatar: fileName },
        );
        mutate(['orginfo', resolvedOrganization.id]);
        mutate('organizations');
        mutate('organizations-b2c');
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (!e.target.files?.length) return;
        const nextFile = e.target.files[0];
        setFile(nextFile);
        setPreview(URL.createObjectURL(nextFile));
    };

    const removeAvatar = async () => {
        setFile(undefined);
        if (preview) {
            URL.revokeObjectURL(preview);
        }
        setPreview(avatarDefault);
        const currentAvatar = isSuperuserAvatar
            ? organization?.superuserAvatar
            : organization?.profile.avatar;
        if (organization && currentAvatar) {
            const targetAvatarSAS = await munduClient.getAvatarSas(
                organization.id,
            );
            await storageService.deleteImage(currentAvatar, targetAvatarSAS);
            await munduClient.updateOganization(organization.id, {
                ...(isSuperuserAvatar
                    ? { superuserAvatar: null }
                    : { avatar: null }),
            });
            mutate(['orginfo', organization.id]);
            mutate('organizations');
            mutate('organizations-b2c');
        }
    };

    useEffect(() => {
        setPreview(resolvedSrc);
    }, [resolvedSrc]);

    return {
        uploadAvatar,
        removeAvatar,
        onChange,
        preview,
        file,
    };
};
